import React, { useState } from 'react';
import { Button } from './ui/button';
import { Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Drop-in CSV + PDF export buttons for admin list pages.
 *
 *   <ExportListButtons api={api} resource="industries" filename="industries" />
 *
 * Hits GET /api/admin/export/{resource}.csv or .pdf and triggers a browser download.
 * Both endpoints are guarded by `get_staff_reader`, so CRO/moderator can use too.
 */
export function ExportListButtons({
  api,
  resource,
  filename,
  size = 'sm',
  className = '',
  testIdPrefix,
}) {
  const [busy, setBusy] = useState(null); // 'csv' | 'pdf' | null
  const idp = testIdPrefix || `export-${resource}`;

  const download = async (ext) => {
    setBusy(ext);
    try {
      const res = await api.get(`/api/admin/export/${resource}.${ext}`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: ext === 'csv' ? 'text/csv' : 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      a.download = `${filename || resource}_${stamp}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${ext.toUpperCase()} downloaded`);
    } catch (e) {
      const msg = e.response?.status === 403
        ? "You don't have permission to export"
        : (e.response?.data?.detail || e.message || 'Download failed');
      toast.error(`${ext.toUpperCase()} download failed: ${msg}`);
    }
    setBusy(null);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        size={size}
        variant="ghost"
        onClick={() => download('csv')}
        disabled={!!busy}
        className="text-[#A0B4CC] hover:text-white hover:bg-white/5"
        data-testid={`${idp}-csv`}
      >
        {busy === 'csv'
          ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          : <Download className="w-4 h-4 mr-1.5" />}
        CSV
      </Button>
      <Button
        size={size}
        variant="ghost"
        onClick={() => download('pdf')}
        disabled={!!busy}
        className="text-[#A0B4CC] hover:text-white hover:bg-white/5"
        data-testid={`${idp}-pdf`}
      >
        {busy === 'pdf'
          ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          : <FileText className="w-4 h-4 mr-1.5" />}
        PDF
      </Button>
    </div>
  );
}

export default ExportListButtons;
